import apiClient from './client';
import type { PaginatedResponse } from '@/types/api';
import type {
  Member,
  MemberFilters,
  MemberFormData,
  MemberNote,
  MemberHistory,
  MemberStatusChange,
  BulkUpdateData,
} from '@/types/member';

/** Unwrap Laravel's { data: T } envelope for single-resource responses */
function unwrap<T>(data: T | { data: T }): T {
  if (data && typeof data === 'object' && 'data' in data && !Array.isArray((data as { data: T }).data)) {
    return (data as { data: T }).data;
  }
  return data as T;
}

/** Compute derived fields from nested API data */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeMember(raw: any): Member {
  const m = raw as Member;
  // Compute full_name if missing
  if (!m.full_name) m.full_name = `${m.first_name} ${m.last_name}`.trim();
  // Compute family fields from nested family object
  if (!m.family_name && raw.family?.name) m.family_name = raw.family.name;
  if (!m.family_address && raw.family?.address) m.family_address = raw.family.address;
  if (!m.family_city && raw.family?.city) m.family_city = raw.family.city;
  // Compute sector_name from nested sector object
  if (!m.sector_name && raw.sector?.name) m.sector_name = raw.sector.name;
  // Compute age from birth_date
  if (m.age == null && m.birth_date) {
    const birth = new Date(m.birth_date);
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    const monthDiff = now.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) age--;
    m.age = age;
  }
  return m;
}

export async function getMembers(
  params?: MemberFilters,
): Promise<PaginatedResponse<Member>> {
  // Map frontend param names to backend expectations
  const apiParams: Record<string, unknown> = { ...params };
  if (params?.page_size) {
    apiParams.per_page = params.page_size;
    delete apiParams.page_size;
  }
  if (params?.ordering) {
    const desc = params.ordering.startsWith('-');
    apiParams.sort_by = desc ? params.ordering.slice(1) : params.ordering;
    apiParams.sort_dir = desc ? 'desc' : 'asc';
    delete apiParams.ordering;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = await apiClient.get<any>('/members', { params: apiParams });
  const raw = response.data;

  // Laravel Resource::collection wraps pagination in { data, meta, links }
  // Flatten meta into top-level to match PaginatedResponse<T>
  const meta = raw.meta ?? {};
  const result: PaginatedResponse<Member> = {
    data: (raw.data ?? []).map(normalizeMember),
    current_page: meta.current_page ?? raw.current_page ?? 1,
    last_page: meta.last_page ?? raw.last_page ?? 1,
    per_page: meta.per_page ?? raw.per_page ?? 25,
    total: meta.total ?? raw.total ?? 0,
    from: meta.from ?? raw.from ?? null,
    to: meta.to ?? raw.to ?? null,
    next_page_url: raw.links?.next ?? meta.next_page_url ?? raw.next_page_url ?? null,
    prev_page_url: raw.links?.prev ?? meta.prev_page_url ?? raw.prev_page_url ?? null,
  };
  return result;
}

export async function getMember(id: string): Promise<Member> {
  const response = await apiClient.get(`/members/${id}`);
  return normalizeMember(unwrap(response.data));
}

export interface DuplicateCandidate {
  id: string;
  first_name: string;
  last_name: string;
  middle_name: string | null;
  birth_date: string | null;
  cell_phone: string | null;
  membership_type: string | null;
}

export async function checkDuplicates(params: {
  first_name?: string;
  last_name?: string;
  exclude_id?: string;
}): Promise<DuplicateCandidate[]> {
  const response = await apiClient.get('/members/check-duplicates', { params });
  return response.data;
}

export async function createMember(data: MemberFormData): Promise<Member> {
  const response = await apiClient.post('/members', data);
  return normalizeMember(unwrap(response.data));
}

export async function updateMember(
  id: string,
  data: Partial<MemberFormData>,
): Promise<Member> {
  const response = await apiClient.put(`/members/${id}`, data);
  return unwrap<Member>(response.data);
}

export async function deleteMember(id: string): Promise<void> {
  await apiClient.delete(`/members/${id}`);
}

export async function restoreMember(id: string): Promise<Member> {
  const response = await apiClient.post(`/members/${id}/restore`);
  return unwrap<Member>(response.data);
}

export async function changeStatus(
  id: string,
  data: MemberStatusChange,
): Promise<Member> {
  const response = await apiClient.post(`/members/${id}/change-status`, data);
  return unwrap<Member>(response.data);
}

/** Approve a member record flagged as pending ("neaprobat"). */
export async function approveMember(id: string): Promise<Member> {
  const response = await apiClient.post(`/members/${id}/approve`);
  return normalizeMember(unwrap(response.data));
}

export async function uploadPhoto(id: string, file: File): Promise<Member> {
  const formData = new FormData();
  formData.append('photo', file);
  const response = await apiClient.post(
    `/members/${id}/upload-photo`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return unwrap<Member>(response.data);
}

export async function getMemberHistory(
  id: string,
): Promise<MemberHistory[]> {
  const response = await apiClient.get(`/members/${id}/history`);
  const result = response.data;
  return Array.isArray(result) ? result : (result as { data: MemberHistory[] }).data ?? result;
}

export async function getMemberNotes(id: string): Promise<MemberNote[]> {
  const response = await apiClient.get(`/members/${id}/notes`);
  const result = response.data;
  return Array.isArray(result) ? result : (result as { data: MemberNote[] }).data ?? result;
}

export async function addMemberNote(
  id: string,
  content: string,
): Promise<MemberNote> {
  const response = await apiClient.post(`/members/${id}/notes`, { content });
  return unwrap<MemberNote>(response.data);
}

export async function updateMemberNote(
  memberId: string,
  noteId: string,
  content: string,
): Promise<MemberNote> {
  const response = await apiClient.put(`/members/${memberId}/notes/${noteId}`, { content });
  return unwrap<MemberNote>(response.data);
}

export async function deleteMemberNote(
  memberId: string,
  noteId: string,
): Promise<void> {
  await apiClient.delete(`/members/${memberId}/notes/${noteId}`);
}

export async function bulkUpdateMembers(
  data: BulkUpdateData,
): Promise<void> {
  await apiClient.post('/members/bulk-update', data);
}

export async function exportMembers(
  params?: MemberFilters & { format?: 'excel' | 'csv' },
): Promise<Blob> {
  const response = await apiClient.get('/members/export', {
    params,
    responseType: 'blob',
  });
  return response.data as Blob;
}
